import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';
import { isDonationMode } from '@/config';

/*
  Global chrome, rendered on every route (App.js only conditionally hides the
  Footer, not this Navbar). White bar, full-color logo, brand-palette text.
  Mobile menu + site-mode CTA logic preserved.
*/

const linkStyle = { fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: 600, fontSize: 14.5, color: '#2c5570', textDecoration: 'none' };

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Our Model', href: '/model' },
    { name: 'Research', href: '/research' },
    { name: 'Partner', href: '/partner' },
    { name: 'Volunteer', href: '/volunteer' },
  ];

  const cta = isDonationMode()
    ? { text: 'Donate', href: '/donate', testId: 'nav-donate-button' }
    : { text: 'Join The Movement', href: '/join-the-movement', testId: 'nav-join-button' };

  const handleLinkClick = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); setIsOpen(false); };

  return (
    <nav data-testid="site-nav" style={{ background: '#ffffff', borderBottom: '1px solid #e8ebf0', position: 'sticky', top: 0, zIndex: 50 }}>
      {/* 48px, not 24px: this was the only gutter on the site at 24, which put the logo's
          left edge at 124px while every section's text edge sits at 148px. Ruled 2026-08-17
          to move the navbar rather than every section. */}
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 48px' }}>
        <div style={{ display: 'flex', height: 84, alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" onClick={handleLinkClick} data-testid="logo-link" style={{ display: 'flex', alignItems: 'center' }}>
            <img src="/assets/logo-color.png" alt="The Home Library Project" style={{ height: 68, width: 'auto', display: 'block' }} />
          </Link>

          {/* Desktop */}
          <div className="hidden md:flex" style={{ alignItems: 'center', gap: 30 }}>
            {navigation.map((item) => (
              <Link key={item.name} to={item.href} onClick={handleLinkClick} style={linkStyle} data-testid={`nav-link-${item.name.toLowerCase().replace(/ /g, '-')}`}>{item.name}</Link>
            ))}
            <Link to="/contact" onClick={handleLinkClick} data-testid="nav-contact-button" style={{ display: 'inline-flex', padding: '10px 20px', borderRadius: 999, border: '1px solid rgba(44,85,112,.32)', color: '#2c5570', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: 600, fontSize: 13.5, textDecoration: 'none' }}>Contact</Link>
            <Link to={cta.href} onClick={handleLinkClick} data-testid={cta.testId} style={{ display: 'inline-flex', padding: '11px 22px', borderRadius: 999, background: '#fad53a', color: '#2c5570', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: 700, fontSize: 13.5, textDecoration: 'none' }}>{cta.text}</Link>
          </div>

          {/* Mobile */}
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <button aria-label="Open menu" data-testid="mobile-menu-button" style={{ background: 'transparent', border: 'none', color: '#2c5570', padding: 8, cursor: 'pointer' }}>
                  <Menu style={{ height: 26, width: 26 }} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[380px]" style={{ background: '#ffffff' }}>
                <nav style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 40 }}>
                  {navigation.map((item) => (
                    <Link key={item.name} to={item.href} onClick={handleLinkClick} style={{ ...linkStyle, fontSize: 18 }}>{item.name}</Link>
                  ))}
                  <Link to="/contact" onClick={handleLinkClick} style={{ ...linkStyle, fontSize: 18 }}>Contact</Link>
                  <Link to={cta.href} onClick={handleLinkClick} data-testid={`mobile-${cta.testId}`} style={{ display: 'inline-flex', justifyContent: 'center', marginTop: 14, padding: '13px 22px', borderRadius: 999, background: '#fad53a', color: '#2c5570', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>{cta.text}</Link>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
